import React from 'react'
import { useIntl } from 'react-intl'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import {
  Headline,
  BaseRichTextComponent,
  Table,
  Icons,
  CustomLinkComponent
} from '../../../'
import { useConfig, Region } from 'msp-integrations'
import { ColType, ProductSpecsRenderLinkProps } from './ProductSpecs.types'

export const RenderCol = ({
  content = '',
  headline = '',
  image = { url: '', name: '', showShadow: false },
  side = 'left',
  children
}: ColType & { side: string; children: any }) => {
  const colStart = side === 'left' ? 'col-start-2' : 'col-start-7'

  return (
    <>
      <Headline tag='h4' className={`productSpecsHeadline ${colStart}`}>
        {headline}
      </Headline>
      <div className={`productSpecsContent ${colStart}`}>
        <BaseRichTextComponent data={{ richText: content }} />
      </div>
      <div className={`productSpecsImage ${colStart}`}>
        <div
          className={`productSpecsImageContainer ${
            image.showShadow ? 'shadow-gray94 shadow-xl' : ''
          }`}
        >
          <LazyLoadImage
            className='object-contain'
            src={image.url}
            alt={image.name}
          />
        </div>
      </div>
      <div className={`productSpecsData ${colStart}`}>{children}</div>
    </>
  )
}

export const RenderLink = ({
  text = '',
  icon = '',
  title = '',
  url = ''
}: ProductSpecsRenderLinkProps) => {
  const {
    theme: {
      font: { regular: font }
    }
  } = useConfig()
  const externalReg = new RegExp('^(http|https)://', 'i')

  return (
    <div className='m-auto my-0.5 flex min-h-[44px] items-center'>
      <CustomLinkComponent
        className='bg-offWhite flex h-fit w-full flex-row rounded py-2 pl-6 pr-2'
        href={url}
        uppercase={false}
        inline
        isExternalLink={!!url.match(externalReg)}
      >
        <div
          className={`${font} text-16 tracking-0 leading-24 flex w-1/2 items-center`}
        >
          <p>{title}</p>
        </div>
        <div
          className={`${font} text-15 tracking-0 ml-4 flex w-1/2 items-center hover:underline`}
        >
          {icon && <Icons iconName={icon} size='lg' />}
          <p className='leading-24 ml-3'>{text}</p>
        </div>
      </CustomLinkComponent>
    </div>
  )
}

export const LeftCol = ({
  content = '',
  headline = '',
  image = { url: '', name: '', showShadow: false },
  productSpecFields,
  showBorder = true
}: ColType & {
  productSpecFields: Array<false | { key: string; value: string | number }>
} & { showBorder: boolean }) => {
  const {
    theme: { font }
  } = useConfig()
  return (
    <RenderCol content={content} headline={headline} image={image} side='left'>
      {productSpecFields.length > 0 && (
        <Table
          fields={productSpecFields}
          showBorder={showBorder}
          fieldsStyle='min-h-[44px] mt-0.5'
          titleClasses={font.bold}
          fieldKeyClasses={`${font.medium} text-15`}
          fieldValueClasses={font.regular}
        />
      )}
    </RenderCol>
  )
}

export const RightCol = ({
  content = '',
  headline = '',
  image = { url: '', name: '', showShadow: false },
  links
}: ColType & {
  links: any
}) => {
  const { formatMessage } = useIntl()

  return (
    <RenderCol content={content} headline={headline} image={image} side='right'>
      {links && <Region region={links} noWrapper={true} />}
    </RenderCol>
  )
}

export const productSpecsAccordionStyles = {
  buttonStyle: 'flex w-full px-4 justify-between items-center mb-4',
  openButtonStyle: 'flex w-full px-4 justify-between items-center',
  closeIcon: <Icons iconName='ArrowUp' width='32px' />,
  openIcon: <Icons iconName='ArrowDown' width='32px' />
}
